import { CSSProperties } from 'react';
import { useReduxState } from 'hooks';

interface Props {
  animate?: boolean;
  ariaLabel?: string;
  color?: string;
  horizontal?: boolean;
  label?: string | React.ReactNode;
  labels?: string[];
  labelStyle?: CSSProperties;
  progress?: number;
  progressAnimation?: boolean;
  progressAnimationContrast?: number;
  progressStyle?: CSSProperties;
  progresses?: { color: string; progress: number }[];
  style?: CSSProperties;
}

const ProgressBar = ({
  animate,
  ariaLabel,
  color,
  horizontal = false,
  label,
  labels,
  labelStyle,
  progress,
  progressAnimation = true,
  progressAnimationContrast = 0.2,
  progressStyle,
  progresses,
  style
}: Props) => {
  const { theme } = useReduxState();

  const progressItems = [
    ...(progresses || [{ color, progress }]),
    { color: theme.lightGreyDark, progress: 1 }
  ];

  return (
    <div
      className="progress-bar"
      style={style}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      {label ? (
        <div className="label" style={labelStyle}>
          {label}
        </div>
      ) : labels ? (
        <div className="label labels" style={labelStyle}>
          {labels.map((labelsLabel) => (
            <div className="labels-section" key={labelsLabel}>
              {labelsLabel}
            </div>
          ))}
        </div>
      ) : null}

      <div className="progress" style={progressStyle}>
        <div
          className="progress-fill"
          style={{
            backgroundImage: progressItems
              .map(
                (progressItem, i) =>
                  `linear-gradient(${progressItem.color},${progressItem.color})` +
                  (i < progressItems.length - 2 &&
                  progressItem.progress > 0 &&
                  progressItem.progress < 1
                    ? `,linear-gradient(white,white)`
                    : '')
              )
              .join(','),
            backgroundSize: progressItems
              .map(
                (progressItem, i) =>
                  `${Math.round(progressItem.progress * 100)}% 100%` +
                  (i < progressItems.length - 2 &&
                  progressItem.progress > 0 &&
                  progressItem.progress < 1
                    ? `,calc(${Math.round(
                        progressItem.progress * 100
                      )}% + 1px) 100%`
                    : '')
              )
              .join(','),
            ...(progressAnimation ? {} : { animation: 'none' })
          }}
        >
          {animate ? <div className="animate" /> : null}
        </div>
      </div>

      <style jsx>{`
        .progress-bar {
          display: ${horizontal ? 'flex' : 'block'};
          margin: 3px 0;
        }
        .label {
          color: ${theme.textColor};
          font-size: 1.3rem;
          font-weight: 600;
          line-height: 1;
          margin-right: ${horizontal ? '7px' : '0'};
          padding: 0 1px;
          position: relative;
          top: ${horizontal ? '0' : '-4px'};
        }
        .labels {
          white-space: nowrap;
        }
        .labels-section {
          display: inline-block;
          position: relative;
        }
        .labels-section:not(:first-child) {
          padding-left: 18px;
        }
        .labels-section:not(:first-child)::before {
          color: ${theme.textColorLighter};
          content: '•';
          font-size: 2rem;
          line-height: 0;
          position: absolute;
          top: 5px;
          left: 6px;
        }
        .progress,
        .loading {
          border-radius: 3px;
          height: 6px;
          margin-top: ${horizontal ? '4px' : '0'};
          opacity: 0.7;
          overflow: hidden;
          position: relative;
          width: 100%;
        }
        .progress {
          background-color: ${theme.lightGreyDark};
        }
        @keyframes progressFill {
          0% {
            width: 0;
          }
        }
        .progress-fill {
          animation: progressFill 1s;
          background-repeat: no-repeat;
          height: 100%;
          position: absolute;
          transition: width 0.5s;
          width: 100%;
        }
        @keyframes progressLoading {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 40px 40px;
          }
        }
        .animate {
          animation: progressLoading 2s linear infinite;
          background-size: 40px 40px;
          background-image: linear-gradient(
            -45deg,
            rgba(255, 255, 255, ${progressAnimationContrast}) 25%,
            transparent 25%,
            transparent 50%,
            rgba(255, 255, 255, ${progressAnimationContrast}) 50%,
            rgba(255, 255, 255, ${progressAnimationContrast}) 75%,
            transparent 75%,
            transparent
          );
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 1;
        }
      `}</style>
    </div>
  );
};

export default ProgressBar;
